import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, FileDown, Plus, AlertTriangle, Clock, CheckCircle2, X, History } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { HrDocuments } from "@/components/hr/HrDocuments";
import { HrEvents } from "@/components/hr/HrEvents";
import { HrInfos, CONTRACTS, contractLabel, PractitionerSelect } from "@/components/hr/HrInfos";
import { HrRgpd } from "@/components/hr/HrRgpd";
import { HrTrainings } from "@/components/hr/HrTrainings";
import { HrDeadlines } from "@/components/hr/HrDeadlines";
import { HrExports } from "@/components/hr/HrExports";
import { HrCalendarGrid } from "@/components/hr/HrCalendarGrid";
import { HrPlanning } from "@/components/hr/HrPlanning";
import { HrReleve } from "@/components/hr/HrReleve";
import { HrRegistre } from "@/components/hr/HrRegistre";
import { ComplianceSuggestDialog } from "@/components/compliance/ComplianceSuggestDialog";
import { downloadBlob } from "@/components/hr/hrUtils";

const LEAVE_TYPES = { conge_paye: "Congé payé", rtt: "RTT", sans_solde: "Sans solde", autre: "Autre" };
const ABS_TYPES = {
  maladie: "Maladie", accident_travail: "Accident du travail", maladie_pro: "Maladie professionnelle",
  maternite: "Maternité", paternite: "Paternité", conge_parental: "Congé parental", sans_solde: "Sans solde",
  autorisee: "Absence autorisée", formation: "Formation", evenement_familial: "Événement familial",
  recuperation: "Récupération", autre: "Autre",
};
const LEAVE_STATUS = { brouillon: "Brouillon", demande: "Demandé", en_attente: "En attente", approuve: "Approuvé", refuse: "Refusé", annule: "Annulé" };
const STATUS_COLOR = { approuve: "bg-emerald-100 text-emerald-700", refuse: "bg-rose-100 text-rose-700", en_attente: "bg-amber-100 text-amber-700", demande: "bg-sky-100 text-sky-700", annule: "bg-gray-100 text-gray-500", brouillon: "bg-gray-100 text-gray-500" };
const URG = { urgent: "bg-rose-100 text-rose-700", bientot: "bg-amber-100 text-amber-700", ok: "bg-emerald-100 text-emerald-700" };
const SUBS = [["dash", "Tableau de bord"], ["emps", "Salariés & collaborateurs"], ["leaves", "Congés"], ["abs", "Absences"], ["cal", "Calendrier"], ["plan", "Planning"], ["train", "Formations"], ["dead", "Échéances"], ["reg", "Registre du personnel"], ["exp", "Exports & RGPD"]];

export default function RH() {
  const { user } = useAuth();
  const canPlanning = ["owner", "hr"].includes(user?.role);
  const canRegistre = ["owner", "admin", "hr"].includes(user?.role);
  const tabs = SUBS.filter(([k]) => (k !== "plan" || canPlanning) && (k !== "reg" || canRegistre));
  const [sub, setSub] = useState("dash");
  const [dash, setDash] = useState(null);
  const [emps, setEmps] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [empDlg, setEmpDlg] = useState(false);
  const [suggestFor, setSuggestFor] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [leaveDlg, setLeaveDlg] = useState(false);
  const [absDlg, setAbsDlg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setDenied(false);
    try {
      const [d, e, l, a] = await Promise.all([
        api.get("/hr/dashboard"), api.get("/hr/employees"), api.get("/hr/leaves"), api.get("/hr/absences"),
      ]);
      setDash(d.data); setEmps(e.data); setLeaves(l.data); setAbsences(a.data);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true); else toast.error(apiErr(err));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ename = (id) => { const e = emps.find((x) => x.id === id); return e ? (e.name || `${e.first_name || ""} ${e.last_name || ""}`.trim()) : "—"; };

  if (denied) return <div className={`${CARD} p-8 text-center`} data-testid="rh-denied"><AlertTriangle className="mx-auto mb-2 h-8 w-8 text-amber-500" /><p className="font-semibold">Accès refusé</p><p className="text-sm text-gray-400">Vous n'avez pas les droits pour accéder au module Ressources humaines.</p></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-sm text-gray-400"><Users className="h-4 w-4" /> Ressources humaines</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Cabinet Ida Gray</h1>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {tabs.map(([k, label]) => (
          <button key={k} data-testid={`rh-tab-${k}`} onClick={() => setSub(k)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${sub === k ? "bg-[#065F46] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="mt-6"><Loader /></div> : (
        <div className="mt-5">
          {sub === "dash" && dash && (
            <div className="space-y-5" data-testid="rh-dashboard">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {[["Effectif actif", dash.effectif.total], ["CDI", dash.effectif.cdi], ["CDD", dash.effectif.cdd], ["Temps plein", dash.effectif.temps_plein], ["Temps partiel", dash.effectif.temps_partiel], ["Absents", dash.effectif.absents]].map(([l, v]) => (
                  <div key={l} className={`${CARD} p-4`}><p className="text-xs text-gray-400">{l}</p><p className="font-display text-2xl font-black text-[#065F46]">{v}</p></div>
                ))}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className={`${CARD} p-5`}>
                  <Label className="mb-2">Aujourd'hui</Label>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="font-display text-xl font-bold text-emerald-600">{dash.aujourdhui.presents}</p><p className="text-xs text-gray-400">Présents</p></div>
                    <div><p className="font-display text-xl font-bold text-rose-600">{dash.aujourdhui.absents}</p><p className="text-xs text-gray-400">Absents</p></div>
                    <div><p className="font-display text-xl font-bold text-sky-600">{dash.aujourdhui.en_conge}</p><p className="text-xs text-gray-400">En congé</p></div>
                  </div>
                  <div className="mt-3 border-t border-gray-100 pt-2 text-sm text-gray-500">{dash.conges.en_attente} demande(s) de congé en attente</div>
                </div>
                <div className={`${CARD} p-5`}>
                  <Label className="mb-2">Échéances RH</Label>
                  {dash.echeances.length === 0 ? <p className="text-sm text-gray-400">Aucune échéance.</p> : (
                    <div className="space-y-1.5">
                      {dash.echeances.slice(0, 8).map((d, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate">{d.label || d.type}</span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${URG[d.urgency] || URG.ok}`}>{d.date ? fmtDate(d.date) : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {sub === "emps" && (
            <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-employees">
              <div className="mb-3 flex items-center justify-between"><Label>Salariés & collaborateurs</Label>
                <Button data-testid="rh-add-emp" onClick={() => setEmpDlg(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1 h-4 w-4" />Ajouter</Button></div>
              {emps.length === 0 ? <EmptyState title="Aucun salarié" subtitle="Ajoutez votre premier salarié." /> : (
                <div className="divide-y divide-gray-50">
                  {emps.map((e) => (
                    <button key={e.id} data-testid={`rh-emp-${e.id}`} onClick={() => setFiche(e)} className="flex w-full items-center gap-3 py-3 text-left hover:bg-gray-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 font-bold text-[#065F46]">{(e.name || "?").charAt(0).toUpperCase()}</div>
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{e.name || `${e.first_name || ""} ${e.last_name || ""}`}</p><p className="text-xs text-gray-400">{e.poste || "—"} · {contractLabel(e.contract_type) || "—"}{e.anciennete ? ` · ${e.anciennete.years} an(s) ${e.anciennete.months} mois` : ""}</p></div>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${e.hr_status === "sorti" ? "bg-gray-100 text-gray-500" : e.hr_status === "suspendu" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{e.hr_status || "actif"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {sub === "leaves" && (
            <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-leaves">
              <div className="mb-3 flex items-center justify-between"><Label>Congés</Label>
                <Button data-testid="rh-add-leave" onClick={() => setLeaveDlg(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1 h-4 w-4" />Nouvelle demande</Button></div>
              {leaves.length === 0 ? <EmptyState title="Aucun congé" subtitle="Créez une demande de congé." /> : (
                <div className="divide-y divide-gray-50">
                  {leaves.map((l) => (
                    <div key={l.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`rh-leave-${l.id}`}>
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{ename(l.employee_id)} · {LEAVE_TYPES[l.type] || l.type}</p>
                        <p className="text-xs text-gray-400">{fmtDate(l.start)} → {fmtDate(l.end)} · {l.days} j · Demandé le {l.requested_at ? fmtDate(l.requested_at) : "—"}{l.decided_at ? ` · Décision le ${fmtDate(l.decided_at)} (${l.decided_by || ""})` : ""}</p></div>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_COLOR[l.status]}`}>{LEAVE_STATUS[l.status] || l.status}</span>
                      {["demande", "en_attente"].includes(l.status) && (
                        <div className="flex gap-1">
                          <button data-testid={`rh-approve-${l.id}`} onClick={() => setStatus(l.id, "approuve")} className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></button>
                          <button data-testid={`rh-refuse-${l.id}`} onClick={() => setStatus(l.id, "refuse")} className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {sub === "abs" && (
            <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-absences">
              <div className="mb-3 flex items-center justify-between"><Label>Absences</Label>
                <Button data-testid="rh-add-abs" onClick={() => setAbsDlg(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1 h-4 w-4" />Nouvelle absence</Button></div>
              {absences.length === 0 ? <EmptyState title="Aucune absence" subtitle="Enregistrez une absence." /> : (
                <div className="divide-y divide-gray-50">
                  {absences.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 py-3" data-testid={`rh-abs-${a.id}`}>
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{ename(a.employee_id)} · {ABS_TYPES[a.category] || a.category}</p>
                        <p className="text-xs text-gray-400">{fmtDate(a.start)} → {fmtDate(a.end)}{a.justificatif_recu ? " · justificatif reçu" : ""}</p></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {sub === "cal" && <HrCalendarGrid emps={emps} />}
          {sub === "plan" && canPlanning && <><HrPlanning /><HrReleve /></>}

          {sub === "train" && <HrTrainings emps={emps} ename={ename} onChanged={load} />}
          {sub === "dead" && <HrDeadlines emps={emps} ename={ename} onChanged={load} />}
          {sub === "reg" && canRegistre && <HrRegistre emps={emps} onOpen={setFiche} />}
          {sub === "exp" && <HrExports ename={ename} />}
        </div>
      )}

      <EmployeeDialog open={empDlg} onOpenChange={setEmpDlg} onSaved={(e) => { load(); if (e?.id) setSuggestFor(e); }} />
      <ComplianceSuggestDialog emp={suggestFor} onClose={() => setSuggestFor(null)} />
      <FicheDialog key={fiche?.id} emp={fiche} onClose={() => setFiche(null)} onChanged={load} />
      <LeaveDialog open={leaveDlg} onOpenChange={setLeaveDlg} emps={emps} onSaved={load} />
      <AbsenceDialog open={absDlg} onOpenChange={setAbsDlg} emps={emps} onSaved={load} />
    </div>
  );

  async function setStatus(id, status) {
    try { await api.put(`/hr/leaves/${id}/status`, { status }); toast.success(status === "approuve" ? "Congé approuvé" : "Congé refusé"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  }
}

function EmployeeDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", poste: "", contract_type: "CDI", date_entree: "", temps_plein: true, hr_status: "actif" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.first_name || !f.last_name) { toast.error("Nom et prénom obligatoires"); return; }
    try { const r = await api.post("/hr/employees", f); toast.success(f.contract_type === "collaborateur" ? "Collaborateur ajouté" : "Salarié créé"); onOpenChange(false); setF({ first_name: "", last_name: "", poste: "", contract_type: "CDI", date_entree: "", temps_plein: true, hr_status: "actif" }); onSaved(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="rh-emp-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">{f.contract_type === "collaborateur" ? "Nouveau collaborateur" : "Nouveau salarié"}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 pt-2">
        <div><Label>Prénom *</Label><Input data-testid="rh-emp-first" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        <div><Label>Nom *</Label><Input data-testid="rh-emp-last" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        <div><Label>Poste</Label><Input value={f.poste} onChange={(e) => set("poste", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        <div><Label>Statut / contrat</Label>
          <Select value={f.contract_type} onValueChange={(v) => set("contract_type", v)}><SelectTrigger data-testid="rh-emp-contract" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{CONTRACTS.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent></Select></div>
        {f.contract_type === "collaborateur" && <div className="col-span-2"><Label>Fiche praticien (Rétrocessions)</Label><PractitionerSelect value={f.practitioner_id} onChange={(v) => set("practitioner_id", v)} /></div>}
        <div><Label>Date d'entrée</Label><Input type="date" value={f.date_entree} onChange={(e) => set("date_entree", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        <div><Label>Temps de travail</Label>
          <Select value={f.temps_plein ? "plein" : "partiel"} onValueChange={(v) => set("temps_plein", v === "plein")}><SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="plein">Temps plein</SelectItem><SelectItem value="partiel">Temps partiel</SelectItem></SelectContent></Select></div>
      </div>
      <Button data-testid="rh-emp-submit" onClick={submit} className="mt-4 w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">{f.contract_type === "collaborateur" ? "Ajouter le collaborateur" : "Créer le salarié"}</Button>
    </DialogContent></Dialog>
  );
}

function FicheDialog({ emp, onClose, onChanged }) {
  const { user } = useAuth();
  const canDocs = ["owner", "admin", "hr"].includes(user?.role);
  const canWrite = canDocs;
  const [tab, setTab] = useState("vue");
  const [bal, setBal] = useState(null);
  const [tl, setTl] = useState([]);
  const [corr, setCorr] = useState({ acquis: "", pris: "", planifie: "", motif: "" });
  useEffect(() => {
    if (emp) {
      setTab("vue");
      api.get(`/hr/employees/${emp.id}/balance`).then((r) => { setBal(r.data); setCorr({ acquis: r.data.acquis, pris: r.data.pris, planifie: r.data.planifie, motif: "" }); }).catch(() => {});
      api.get(`/hr/employees/${emp.id}/timeline`).then((r) => setTl(r.data)).catch(() => {});
    }
  }, [emp]);
  if (!emp) return null;
  const saveCorr = async () => {
    try { const r = await api.put(`/hr/employees/${emp.id}/balance`, corr); setBal({ ...bal, ...r.data, solde: r.data.acquis - r.data.pris - r.data.planifie }); toast.success("Compteur corrigé"); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={!!emp} onOpenChange={(v) => !v && onClose()}><DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-2xl" data-testid="rh-fiche">
      <DialogHeader><DialogTitle className="font-display text-xl">{emp.name || `${emp.first_name} ${emp.last_name}`}</DialogTitle></DialogHeader>
      <p className="text-sm text-gray-400">{emp.poste || "—"} · {contractLabel(emp.contract_type) || "—"} · {emp.anciennete ? `${emp.anciennete.years} an(s) ${emp.anciennete.months} mois` : "ancienneté N/A"} · {emp.hr_status || "actif"}</p>
      {canDocs && (
        <Button data-testid="rh-fiche-pdf" variant="outline" onClick={() => downloadBlob(`/hr/employees/${emp.id}/fiche.pdf`, `dossier-rh-${(emp.name || "salarie").replace(/\s+/g, "-")}.pdf`).catch((e) => toast.error(apiErr(e)))} className="mt-2 w-fit rounded-full"><FileDown className="mr-1 h-4 w-4" />Exporter le dossier RH (PDF)</Button>
      )}
      {bal && (
        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
          {[["Acquis", bal.acquis], ["Pris", bal.pris], ["Planifié", bal.planifie], ["Solde", bal.solde]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-gray-50 p-2"><p className="text-[11px] text-gray-400">{l}</p><p className="font-display text-lg font-bold text-[#065F46]">{v}</p></div>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {[["vue", "Compteurs"], ...(canWrite ? [["info", "Infos"]] : []), ...(canDocs ? [["docs", "Documents"]] : []), ...(canWrite ? [["events", "Événements"]] : []), ["hist", "Historique"], ["rgpd", "RGPD"]].map(([k, l]) => (
          <button key={k} data-testid={`rh-fiche-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-[#065F46] text-white" : "bg-gray-100 text-gray-600"}`}>{l}</button>
        ))}
      </div>
      {tab === "vue" && (
        <div className="mt-3 space-y-2 rounded-2xl bg-amber-50 p-3">
          <Label>Correction manuelle des compteurs</Label>
          <div className="grid grid-cols-3 gap-2">
            <div><p className="text-xs text-gray-500">Acquis</p><Input data-testid="rh-corr-acquis" type="number" value={corr.acquis} onChange={(e) => setCorr({ ...corr, acquis: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><p className="text-xs text-gray-500">Pris</p><Input type="number" value={corr.pris} onChange={(e) => setCorr({ ...corr, pris: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><p className="text-xs text-gray-500">Planifié</p><Input type="number" value={corr.planifie} onChange={(e) => setCorr({ ...corr, planifie: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <Input placeholder="Motif de la correction" value={corr.motif} onChange={(e) => setCorr({ ...corr, motif: e.target.value })} className="rounded-xl" />
          <Button data-testid="rh-corr-save" onClick={saveCorr} className="w-full rounded-full bg-[#065F46] hover:bg-[#047857]">Enregistrer la correction</Button>
        </div>
      )}
      {tab === "info" && <HrInfos emp={emp} onSaved={onChanged} />}
      {tab === "docs" && <HrDocuments empId={emp.id} />}
      {tab === "events" && <HrEvents empId={emp.id} onChanged={() => api.get(`/hr/employees/${emp.id}/timeline`).then((r) => setTl(r.data)).catch(() => {})} />}
      {tab === "rgpd" && <HrRgpd emp={emp} onChanged={() => { onChanged && onChanged(); onClose(); }} />}
      {tab === "hist" && (
        <div className="mt-3 space-y-2" data-testid="rh-timeline">
          {tl.length === 0 ? <p className="text-sm text-gray-400">Aucun historique.</p> : tl.map((t, i) => (
            <div key={i} className="flex items-start gap-2 text-sm"><History className="mt-0.5 h-3.5 w-3.5 text-gray-400" /><span className="text-gray-400">{t.date ? fmtDate(t.date) : ""}</span><span>{t.label}</span></div>
          ))}
        </div>
      )}
    </DialogContent></Dialog>
  );
}

function LeaveDialog({ open, onOpenChange, emps, onSaved }) {
  const [f, setF] = useState({ employee_id: "", type: "conge_paye", start: "", end: "", days: "" });
  const [conf, setConf] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.employee_id || !f.start || !f.end) { toast.error("Salarié, début et fin obligatoires"); return; }
    try { const r = await api.post("/hr/leaves", f); setConf(r.data.conflicts || []); toast.success("Demande enregistrée"); if (!(r.data.conflicts || []).length) { onOpenChange(false); setF({ employee_id: "", type: "conge_paye", start: "", end: "", days: "" }); } onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="rounded-3xl sm:max-w-md" data-testid="rh-leave-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">Demande de congé</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div><Label>Salarié *</Label><Select value={f.employee_id || undefined} onValueChange={(v) => set("employee_id", v)}><SelectTrigger data-testid="rh-leave-emp" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(LEAVE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label>Début *</Label><Input data-testid="rh-leave-start" type="date" value={f.start} onChange={(e) => set("start", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Fin *</Label><Input data-testid="rh-leave-end" type="date" value={f.end} onChange={(e) => set("end", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Jours</Label><Input type="number" value={f.days} onChange={(e) => set("days", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        </div>
        {conf.length > 0 && (
          <div className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="rh-leave-conflict"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>Attention : {conf.length} autre(s) salarié(s) déjà absent(s) sur cette période.</span></div>
        )}
        <Button data-testid="rh-leave-submit" onClick={submit} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">Enregistrer</Button>
      </div>
    </DialogContent></Dialog>
  );
}

function AbsenceDialog({ open, onOpenChange, emps, onSaved }) {
  const [f, setF] = useState({ employee_id: "", category: "maladie", start: "", end: "", justificatif_recu: false, comment: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.employee_id || !f.start || !f.end) { toast.error("Salarié, début et fin obligatoires"); return; }
    try { await api.post("/hr/absences", f); toast.success("Absence enregistrée"); onOpenChange(false); setF({ employee_id: "", category: "maladie", start: "", end: "", justificatif_recu: false, comment: "" }); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="rh-abs-dialog">
      <DialogHeader><DialogTitle className="font-display text-xl">Nouvelle absence</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div><Label>Salarié *</Label><Select value={f.employee_id || undefined} onValueChange={(v) => set("employee_id", v)}><SelectTrigger data-testid="rh-abs-emp" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Catégorie</Label><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="rh-abs-cat" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ABS_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Début *</Label><Input data-testid="rh-abs-start" type="date" value={f.start} onChange={(e) => set("start", e.target.value)} className="mt-1.5 rounded-xl" /></div>
          <div><Label>Fin *</Label><Input data-testid="rh-abs-end" type="date" value={f.end} onChange={(e) => set("end", e.target.value)} className="mt-1.5 rounded-xl" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.justificatif_recu} onChange={(e) => set("justificatif_recu", e.target.checked)} className="h-4 w-4 accent-[#065F46]" /> Justificatif reçu</label>
        <div><Label>Commentaire (factuel)</Label><Input value={f.comment} onChange={(e) => set("comment", e.target.value)} className="mt-1.5 rounded-xl" />
          {f.category === "maladie" && <p className="mt-1 text-xs text-amber-600" data-testid="rh-health-warning">Ne renseignez aucune information relative au diagnostic ou à la pathologie du salarié.</p>}</div>
        <Button data-testid="rh-abs-submit" onClick={submit} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">Enregistrer l'absence</Button>
      </div>
    </DialogContent></Dialog>
  );
}
